import * as React from 'react';
import {useHistory} from 'react-router-dom';
import {debounce} from 'lodash-es';
import userRoutes from 'routes/admin.routes';
import NotAuthorized from 'components/notAuthorized';
import {useFindQuery, useUpdateMutation} from 'features/users/usersApi';
import {TableActions} from 'components/tableActions';

import {
  Box,
  Cluster,
  Cover,
  Heading,
  Notification,
  Paragraph,
  Spinner,
  Table,
  TdLink,
  Template,
  TextInput,
  Toggle,
  Toast,
  trimModel,
  ITableCol,
} from '@pluto-tv/assemble';

import {IUser} from 'models/users';
import {useAppPermissions} from 'app/permissions';
import {withThousandsSeparator} from 'utils/thousands-separator';
import {getPrefixedUrl} from 'routes';

interface IUserToggle {
  row: IUser;
  onChange: (val: boolean, row: IUser) => void;
}

const UserActiveView = React.memo(({row}: IUserToggle) => <>{row.active ? 'Active' : 'Inactive'}</>);

const UserActiveToggle = React.memo(({row, onChange}: IUserToggle) => {
  const [userActive, setUserActive] = React.useState(row.active);
  const [label, setLabel] = React.useState(row.active ? 'Active' : 'Inactive');

  const toggleChanged = async (val: boolean) => {
    try {
      await onChange(val, row);
      setLabel(val ? 'Active' : 'Inactive');
    } catch (e) {
      // This will trigger the Toggle to switch back when it fails
      setUserActive(val);
      setTimeout(() => {
        setUserActive(!val);
        setLabel(!val ? 'Active' : 'Inactive');
      });
    }
  };

  React.useEffect(() => {
    setUserActive(row.active);
    setLabel(row.active ? 'Active' : 'Inactive');
  }, [row]);

  return <Toggle value={userActive} onChange={val => toggleChanged(val)} label={label} id='toggleUserActive'></Toggle>;
});

const ROWS_PER_PAGE = 25;

export default (): JSX.Element => {
  const history = useHistory();
  const [page, setPage] = React.useState(0);
  const [allUsers, setAllUsers] = React.useState<IUser[]>([]);
  const [search, setSearch] = React.useState<string>('');
  const currentSearchTerm = React.useRef<string>('');
  const searchResultsReceived = React.useRef<boolean>(false);
  const blockLazyLoad = React.useRef<boolean>(false);

  const {ableTo} = useAppPermissions();
  const canUserEdit = ableTo('USER_EDIT');
  const [updateUser] = useUpdateMutation();

  if (!ableTo('USER_VIEW')) {
    return <NotAuthorized />;
  }

  const {data: users, isFetching} = useFindQuery(
    {
      offset: page * ROWS_PER_PAGE,
      limit: ROWS_PER_PAGE,
      search: search,
    },
    {refetchOnMountOrArgChange: true},
  );

  React.useLayoutEffect(() => {
    const searchUserInput = document.getElementById('userSearchField');
    searchUserInput?.focus({preventScroll: true});
  }, []);

  const handleEdit = (user: IUser) => {
    history.push(userRoutes.paths.userEditPage.replace(':id', user._id));
  };

  const doSearch = debounce((val: string) => {
    blockLazyLoad.current = true;
    searchResultsReceived.current = false;
    currentSearchTerm.current = val;
    setPage(0);
    setAllUsers([]);
    setSearch(val);
  }, 500);

  const toggleActiveUser = async (val: boolean, user: IUser) => {
    if (!canUserEdit) return;

    const {_id: id, ...props} = trimModel(user, 'displayName', 'email');

    try {
      const res = await updateUser({
        id,
        user: {
          ...props,
          active: val,
        },
      });

      if ((res as any).error) {
        throw Error((res as any).error);
      }

      Toast.success('Success', 'User Updated');
    } catch (e) {
      Toast.error('Error', 'Error Updating User');
      throw Error();
    }
  };

  React.useEffect(() => {
    if (users?.data) {
      if (page === 0 && search === currentSearchTerm.current) {
        searchResultsReceived.current = true;
        blockLazyLoad.current = false;
      }

      setAllUsers(prevUsers => {
        const uniqueUsers = page === 0 ? [] : [...prevUsers];
        users.data.forEach(newUser => {
          if (!uniqueUsers.some(existing => existing._id === newUser._id)) {
            uniqueUsers.push(newUser);
          }
        });
        return uniqueUsers;
      });
    }
  }, [users?.data, page, search]);

  React.useEffect(() => {
    if (users?.data && users.data.length > 0 && !searchResultsReceived.current) {
      searchResultsReceived.current = true;
      blockLazyLoad.current = false;
      currentSearchTerm.current = search;
    }
  }, [users?.data, search]);

  const canLoadMore = React.useMemo(() => {
    return (
      users?.metadata &&
      allUsers.length < users.metadata.totalCount &&
      !blockLazyLoad.current &&
      searchResultsReceived.current
    );
  }, [users?.metadata, allUsers.length]);

  const onLazyLoad = React.useCallback(() => {
    if (!isFetching && canLoadMore && search === currentSearchTerm.current) {
      setPage(prevPage => prevPage + 1);
    }
  }, [isFetching, canLoadMore, search]);

  return (
    <>
      <Cover scrolling={true} gutter='large' coverTemplateHeight='100%' padding={{mobile: 'medium', wide: 'large'}}>
        <Template label='header'>
          <Cluster justify='space-between' align='center' space='medium'>
            <Cluster align='end' space='small'>
              <Heading level='h1'>Users</Heading>
              <Paragraph color='secondary'>{withThousandsSeparator(users?.metadata.totalCount || 0)} Items</Paragraph>
            </Cluster>
            <Cluster space='small'>
              <TextInput
                id='userSearchField'
                iconLeft='search'
                size='small'
                placeholder='Search Users'
                onChange={val => doSearch(val)}
              />
            </Cluster>
          </Cluster>
        </Template>
        <Template label='cover'>
          <Box
            background='pewter'
            borderTop={true}
            borderSize='0.125rem'
            borderColor='cavern'
            paddingTop={(allUsers?.length as number) > 0 ? 'xsmall' : 'medium'}
            paddingBottom='none'
            paddingX='large'
            fullHeight={true}
          >
            <Table
              id='usersTable'
              loading={isFetching && page === 0}
              fixedHeader={true}
              lazyLoading={isFetching && page !== 0}
              onLazyLoad={onLazyLoad}
              lazyLoadingMsg='Loading more users...'
              cols={[
                {
                  label: 'Name',
                  transform: row => (
                    <TdLink
                      row={row}
                      title={row.displayName}
                      url={getPrefixedUrl(userRoutes.paths.userEditPage.replace(':id', row._id))}
                      onClick={handleEdit}
                    />
                  ),
                },
                {
                  label: 'Email',
                  field: 'email',
                },
                {
                  label: 'Role(s)',
                  colMaxWidth: '125rem',
                  transform: row => (row && row.roles && row.roles.length > 0 ? row.roles.join(', ') : ''),
                },
                {
                  label: 'Account Created',
                  transform: row =>
                    new Date(row.createdAt).toLocaleString('en-US', {year: 'numeric', month: 'long', day: 'numeric'}),
                },
                {
                  label: 'Status',
                  colWidth: '9.375rem',
                  transform: row =>
                    canUserEdit ? (
                      <UserActiveToggle row={row} onChange={(val: boolean, row) => toggleActiveUser(val, row)} />
                    ) : (
                      <UserActiveView row={row} onChange={(val: boolean, row) => toggleActiveUser(val, row)} />
                    ),
                },
                ...(canUserEdit
                  ? [
                      {
                        label: 'Actions',
                        colWidth: '6.25rem',
                        transform: row => (
                          <TableActions
                            icons={canUserEdit ? ['edit'] : []}
                            row={row}
                            onClick={row => handleEdit(row)}
                          />
                        ),
                      } as ITableCol<IUser>,
                    ]
                  : []),
              ]}
              rows={allUsers}
            >
              <Template label='loading'>
                <Cluster space='small' align='center'>
                  <Spinner />
                  <Paragraph>Loading Users</Paragraph>
                </Cluster>
              </Template>
              <Template label='empty'>
                <Notification type='warning'>There are no users currently available.</Notification>
              </Template>
            </Table>
          </Box>
        </Template>
      </Cover>
    </>
  );
};
